import { useSnackbar } from 'notistack';
import {
  TextField, Link, Divider, InputAdornment, OutlinedInput,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import { useState } from 'react';
import { ListComment, ListUser } from 'lib/types';
import { convertZuluDate } from 'lib/utils/datetime';

interface Props {
  listUser: ListUser;
  listId: number;
  onCommentsUpdate?: (updatedComments: ListComment[]) => void;
}

export const ListCommentModal = ({
  listUser,
  listId,
  onCommentsUpdate,
}: Props) => {
  const currentUser = useCurrentUser();
  const API = useApi();
  const [comments, setComments] = useState(listUser.listComments || []);
  const [newComment, setNewComment] = useState<string>('');
  const [editComment, setEditComment] = useState<ListComment | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const onSave = async () => {
    try {
      const response = await API.addCommentToListUser(listId, listUser.userId, newComment);
      const updatedComments = [...comments, response];
      setComments(updatedComments);
      setNewComment('');
      onCommentsUpdate?.(updatedComments);
      enqueueSnackbar('Comment saved successfully!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(`There was an error saving your comment: ${err}`, {
        variant: 'error',
      });
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await API.deleteListComment(listId, commentId);
      const updatedComments = comments.filter((c) => c.id !== commentId);
      setComments(updatedComments);
      onCommentsUpdate?.(updatedComments);
      enqueueSnackbar('Comment deleted successfully!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(`There was an error deleting your comment: ${err}`, {
        variant: 'error',
      });
    }
  };

  const handleUpdateComment = async (
    comment: string, commentId: number,
  ) => {
    try {
      const updatedComment = await API.editListComment(listId, commentId, comment);
      const updatedComments = comments.map((c) => (
        c.id === commentId ? updatedComment : c
      ));
      setComments(updatedComments);
      setEditComment(null);
      onCommentsUpdate?.(updatedComments);
      enqueueSnackbar('Comment updated successfully!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(`There was an error updating your comment: ${err}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <div className="p-6">
        <h4 className="text-xl mb-2 font-bold">Comments</h4>
        {comments?.map((c) => (
          <div key={c.id} className="mb-4">
            <p>
              {c.id === editComment?.id ? (
                <OutlinedInput
                  id={`editComment-${c.id}`}
                  label="Edit Comment"
                  multiline
                  rows={2}
                  defaultValue={c.comment}
                  fullWidth
                  onBlur={async (e) => handleUpdateComment(e.target.value, c.id)}
                  value={editComment.comment}
                  onChange={(e) => setEditComment({ ...editComment, comment: e.target.value })}
                  endAdornment={(
                    <InputAdornment position="end">
                      <button
                        onClick={async () => handleUpdateComment(editComment.comment, c.id)}
                        type="button"
                        className="btn-primary-xs"
                      >
                        Save
                      </button>
                    </InputAdornment>
                  )}
                />
              ) : (
                <>
                  {c.commenter.username ? (
                    <Link
                      href={`/${c.commenter.username}`}
                    >
                      <strong>{c.commenter.name}</strong>
                    </Link>
                  ) : (
                    <strong>{c.commenter.name}</strong>
                  )}
                  {' '}
                  -
                  {' '}
                  {c.comment}
                </>
              )}
            </p>
            <p className="text-sm text-right text-gray-600">
              {c.commenter.id === currentUser?.id && (
                <div>
                  <button type="button" className="mr-2" onClick={() => setEditComment(c)}>
                    <EditIcon fontSize="small" />
                  </button>
                  <button type="button" onClick={() => handleDeleteComment(c.id)}>
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              )}
              {convertZuluDate(c.createdAt)}
            </p>
            <Divider className="my-2" />
          </div>
        ))}
        <TextField
          variant="outlined"
          id="newComment"
          label="New Comment"
          multiline
          rows={2}
          value={newComment}
          fullWidth
          onChange={(e) => setNewComment(e.target.value)}
        />
        <div className="mt-4 text-right">
          <span className="mr-2">
            <button
              className="btn-primary"
              onClick={onSave}
              type="button"
              disabled={!newComment.length}
            >
              Save
            </button>
          </span>
        </div>
      </div>
    </>
  );
};
